// import type { AuthCredentials } from '@/domains/auth/auth.entity';
// import type { AuthRepository } from '@/domains/auth/auth.repository';
// import type { AuthStoreRepository } from '@/shared/contracts/authStore.repository';
// import { mapAuthResultToAuthUserEntityStore } from '@/shared/mappers';
//
// /**
//  * Use Case para el proceso completo de autenticación (login)
//  * Cumple SRP: Solo se encarga de orquestar el flujo de login
//  * Cumple DIP: Depende de abstracciones (AuthRepository, AuthStoreRepository)
//  *
//  * Responsabilidades:
//  * - Autenticar credenciales con la API
//  * - Transformar respuesta de dominio a modelo de store
//  * - Persistir datos de sesión en el store (con encriptación automática)
//  */
// export class LoginUseCase {
//   private authRepository: AuthRepository;
//   private authStoreRepository: AuthStoreRepository;
//
//   /**
//    * Inicializa el UseCase con los repositorios necesarios
//    * @param authRepository - Repositorio para autenticación con API
//    * @param authStoreRepository - Repositorio para persistencia de datos de sesión
//    */
//   constructor(
//     authRepository: AuthRepository,
//     authStoreRepository: AuthStoreRepository,
//   ) {
//     this.authRepository = authRepository;
//     this.authStoreRepository = authStoreRepository;
//   }
//
//   /**
//    * Ejecuta el proceso completo de login
//    * Flujo: 1) Autenticar con API, 2) Transformar datos, 3) Persistir en store
//    * @param credentials - Credenciales del usuario (usuario y contraseña)
//    * @returns Promesa que se resuelve cuando el login es exitoso
//    * @throws Error si las credenciales son inválidas o falla la autenticación
//    */
//   async execute(credentials: AuthCredentials): Promise<void> {
//     const authResponse = await this.authRepository.login(credentials);
//     const storeData = mapAuthResultToAuthUserEntityStore(authResponse);
//     this.authStoreRepository.saveAuthData(storeData);
//   }
// }

import type {
  AuthCredentials,
  AuthDataResponse,
} from '@/domains/auth/auth.entity';
import type { AuthRepository } from '@/domains/auth/auth.repository';
import type { AuthStoreRepository } from '@/shared/contracts/authStore.repository';
import { mapAuthResultToAuthUserEntityStore } from '@/shared/mappers';

/**
 * Use Case para el proceso completo de autenticación (login) del usuario.
 *
 * Funcionalidad:
 * - Autentica las credenciales del usuario contra el backend
 * - Retorna los datos de autenticación sin persistir automáticamente
 * - Proporciona método separado para guardar datos en el store cuando sea necesario
 * - Permite al hook de UI decidir cuándo persistir según validaciones (ej: ultimo_acceso)
 *
 * Responsabilidades:
 * - Coordinar autenticación de credenciales con el repositorio de auth
 * - Transformar respuesta de dominio a modelo de store mediante mapper
 * - Delegar decisión de persistencia a la capa de presentación
 * - Actuar como intermediario entre dominio, infraestructura y estado global
 */
export class LoginUseCase {
  private authRepository: AuthRepository;
  private authStoreRepository: AuthStoreRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param authRepository - Repositorio para autenticación con la API
   * @param authStoreRepository - Repositorio para persistencia de datos de sesión en el store
   */
  constructor(
    authRepository: AuthRepository,
    authStoreRepository: AuthStoreRepository,
  ) {
    this.authRepository = authRepository;
    this.authStoreRepository = authStoreRepository;
  }

  /**
   * Ejecuta el proceso de autenticación sin persistir automáticamente.
   *
   * @param credentials - Credenciales del usuario (usuario y contraseña)
   */
  async execute(credentials: AuthCredentials): Promise<AuthDataResponse> {
    const authResponse = await this.authRepository.login(credentials);

    return authResponse;
  }

  /**
   * Persiste los datos de autenticación en el store global.
   *
   * @param authData - Datos de autenticación a guardar en el store
   */
  saveToStore(authData: AuthDataResponse): void {
    const storeData = mapAuthResultToAuthUserEntityStore(authData);
    this.authStoreRepository.saveAuthData(storeData);
  }
}
